import { useEffect } from "react";
import { Link } from "wouter";
import { Sparkles, Star } from "lucide-react";
import menPeople40Img from "../../assets/images/landing_m40.webp";
import menPeople2039Img from "../../assets/images/landing_m20.webp";
import sapphicPeopleImg from "../../assets/images/landing_sapphic.webp";
import menPeople50Img from "../../assets/images/landing_m50.webp";
import women40Img from "../../assets/images/landing_w40.webp";
import coffeeSapphicImg from "../../assets/images/landing_coffee_sapphic.webp";
import coffeeSapphic40Img from "../../assets/images/landing_coffee_sapphic40.webp";
import coffeeMen40Img from "../../assets/images/landing_coffee_men40.webp";
import coffeeMen2039Img from "../../assets/images/landing_coffee_men2039.webp";
import tracking from "../../lib/tracking";
import { Trans, useT } from "@/lib/i18n";
import Logo from "../../components/Logo";
import { useMinimalLanding } from "../../lib/features";
import LandingSapphic from "./LandingSapphic";
import LandingMen40 from "./LandingMen40";
import LandingMen2040 from "./LandingMen2040";

const EMPHASIS =
  "underline decoration-primary-500 decoration-[3px] underline-offset-[6px]";

interface LandingMinimalProps {
  variant: "men40" | "men2039" | "sapphic" | "men50" | "women40";
  imageSrc: string;
  imageClassName?: string;
  emphasisClassName?: string;
  posthogEvent: string;
  audienceParam: "m" | "s";
  ownedLabel?: "gay" | "lgbtq";
  /** i18n key prefix for the copy block — "minimal" (dinner) or "minimalCoffee". */
  copyPrefix?: string;
  /** Optional `?et=` value appended to the quiz CTA (e.g. "c" for coffee). */
  eventTypeParam?: "c";
}

function quizHref(audienceParam: string, eventTypeParam?: string): string {
  const { hostname } = window.location;
  const isLocal = hostname === "localhost" || hostname === "127.0.0.1";
  const base = isLocal ? "/app/quiz" : "https://app.outclose.com/quiz";
  const et = eventTypeParam ? `&et=${eventTypeParam}` : "";
  return `${base}?a=${audienceParam}${et}`;
}

function LandingMinimal({
  variant,
  imageSrc,
  imageClassName,
  emphasisClassName,
  posthogEvent,
  audienceParam,
  ownedLabel,
  copyPrefix = "minimal",
  eventTypeParam,
}: LandingMinimalProps) {
  const { t } = useT("landing");

  useEffect(() => {
    tracking.trackPage(posthogEvent);
  }, [posthogEvent]);

  const ctaHref = quizHref(audienceParam, eventTypeParam);

  return (
    <div className="h-full flex flex-col items-center px-6 pt-10 pb-2 overflow-hidden">
      <div className="flex flex-col items-center text-center w-full max-w-sm">
        <Logo withIcon={true} className="mb-4"/>

        <h1 className="text-3xl md:text-4xl font-semibold text-type-950 dark:text-neutral-50 font-Poppins leading-tight mb-4 w-full">
          <Trans
            ns="landing"
            i18nKey={`${copyPrefix}.variants.${variant}.headline`}
            components={{
              emphasis: <span className={emphasisClassName ?? EMPHASIS} />,
            }}
          />
        </h1>

        <div className="relative aspect-square w-full overflow-hidden rounded-2xl shadow-lg">
          <img
            src={imageSrc}
            alt={t(`${copyPrefix}.variants.${variant}.imageAlt`)}
            className={`w-full h-full object-cover ${imageClassName ?? ""}`}
          />
        </div>

        <p className="text-sm font-medium text-dark-muted dark:text-neutral-300 mt-3">
          {t(`${copyPrefix}.variants.${variant}.concept`)}
        </p>

        <p className="text-sm text-dark-muted dark:text-neutral-400 mt-1">
          {t(`${copyPrefix}.subtitle`)}
        </p>

        <a
          href={ctaHref}
          className="rounded-full bg-primary-500 hover:bg-primary-600 text-white font-bold text-lg px-10 py-4 shadow-lg shadow-primary-500/40 w-full sm:w-auto inline-flex items-center justify-center mt-4 transition-colors"
        >
          {t(`${copyPrefix}.cta`)}
          <Sparkles className="inline-block ml-2 w-5 h-5" />
        </a>

        <p className="text-sm flex items-center gap-1.5 text-dark-muted dark:text-neutral-300 mt-3">
          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
          <span>
            <Trans
              ns="landing"
              i18nKey={`${copyPrefix}.rating`}
              components={{ strong: <span className="font-semibold" /> }}
            />
          </span>
        </p>

        <p className="text-xs text-dark-muted dark:text-neutral-500 mt-3">
          <Trans
            ns="landing"
            i18nKey={`${copyPrefix}.terms`}
            components={{
              terms: (
                <Link
                  href="/terms"
                  className="underline hover:text-primary-500 transition-colors"
                />
              ),
              privacy: (
                <Link
                  href="/privacy"
                  className="underline hover:text-primary-500 transition-colors"
                />
              ),
            }}
          />
        </p>

        {ownedLabel && (
          <p className="text-xs flex items-center justify-center gap-1.5 text-dark-muted dark:text-neutral-400 mt-3">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-gradient-to-br from-red-500 via-yellow-400 to-violet-500" />
            {t(ownedLabel === "lgbtq" ? "footer.proudlyLgbtqOwned" : "footer.proudlyGayOwned")}
          </p>
        )}
      </div>
    </div>
  );
}

export function MinimalMen40() {
  const minimalLanding = useMinimalLanding();
  if (minimalLanding === undefined) return null;
  if (!minimalLanding) return <LandingMen40 />;
  return (
    <LandingMinimal
      variant="men40"
      imageSrc={menPeople40Img}
      imageClassName="scale-125 origin-top"
      posthogEvent="minimal_men_40_landing"
      audienceParam="m"
    />
  );
}

export function MinimalMen2039() {
  const minimalLanding = useMinimalLanding();
  if (minimalLanding === undefined) return null;
  if (!minimalLanding) return <LandingMen2040 />;
  return (
    <LandingMinimal
      variant="men2039"
      imageSrc={menPeople2039Img}
      posthogEvent="minimal_men_20_landing"
      audienceParam="m"
    />
  );
}

export function MinimalSapphic() {
  const minimalLanding = useMinimalLanding();
  if (minimalLanding === undefined) return null;
  if (!minimalLanding) return <LandingSapphic />;
  return (
    <LandingMinimal
      variant="sapphic"
      imageSrc={sapphicPeopleImg}
      emphasisClassName={`font-bold ${EMPHASIS}`}
      posthogEvent="minimal_sapphic_landing"
      audienceParam="s"
    />
  );
}

// Always-on minimal landings (not gated behind the minimal_landing flag).
export function MinimalMen50() {
  return (
    <LandingMinimal
      variant="men50"
      imageSrc={menPeople50Img}
      posthogEvent="minimal_men_50_landing"
      audienceParam="m"
      ownedLabel="gay"
    />
  );
}

export function MinimalWomen40() {
  return (
    <LandingMinimal
      variant="women40"
      imageSrc={women40Img}
      emphasisClassName={`font-bold ${EMPHASIS}`}
      posthogEvent="minimal_women_40_landing"
      audienceParam="s"
      ownedLabel="lgbtq"
    />
  );
}

// Coffee-format minimal landings — same template, coffee copy (minimalCoffee.*),
// and a `?et=c` CTA that nudges the quiz into coffee wording. Images reuse the
// community photos until coffee-specific shots are dropped in.
export function MinimalCoffeeMen2039() {
  return (
    <LandingMinimal
      variant="men2039"
      imageSrc={coffeeMen2039Img}
      posthogEvent="minimal_coffee_men_20_landing"
      audienceParam="m"
      copyPrefix="minimalCoffee"
      eventTypeParam="c"
    />
  );
}

export function MinimalCoffeeMen40() {
  return (
    <LandingMinimal
      variant="men40"
      imageSrc={coffeeMen40Img}
      posthogEvent="minimal_coffee_men_40_landing"
      audienceParam="m"
      copyPrefix="minimalCoffee"
      eventTypeParam="c"
    />
  );
}

export function MinimalCoffeeSapphic() {
  return (
    <LandingMinimal
      variant="sapphic"
      imageSrc={coffeeSapphicImg}
      emphasisClassName={`font-bold ${EMPHASIS}`}
      posthogEvent="minimal_coffee_sapphic_landing"
      audienceParam="s"
      copyPrefix="minimalCoffee"
      eventTypeParam="c"
    />
  );
}

// Same sapphic copy, older-audience creative (40+).
export function MinimalCoffeeSapphic40() {
  return (
    <LandingMinimal
      variant="sapphic"
      imageSrc={coffeeSapphic40Img}
      emphasisClassName={`font-bold ${EMPHASIS}`}
      posthogEvent="minimal_coffee_sapphic_40_landing"
      audienceParam="s"
      copyPrefix="minimalCoffee"
      eventTypeParam="c"
    />
  );
}

export default LandingMinimal;
